import { registerBlock } from "../../registry";
import { makeId } from "../../helpers";
import type { ContentBlock } from "../../types";

// ─── Diminished Ovarian Reserve (DOR) ───────────────────────────────

const dor: ContentBlock<"condition"> = {
  id: makeId("dx-diminished-ovarian-reserve"),
  kind: "condition",
  tags: [
    "her",
    "age-factor",
    "testing",
    "ivf",
    "egg-freezing",
    "evidence-based",
  ],
  title: "Diminished ovarian reserve",
  subtitle: "Lower-than-expected egg quantity for age",
  body: {
    conditionName: "Diminished ovarian reserve (DOR)",
    category: "reproductive",
    description:
      "Diminished ovarian reserve means the ovaries have fewer eggs remaining than expected for a woman's age. It does not necessarily mean the eggs are poor quality — quantity and quality are related but separate factors. DOR is one of the most common findings during fertility evaluation.",
    symptoms: [
      "Often no symptoms — found through blood work and ultrasound",
      "Shorter menstrual cycles (e.g. 24–25 days)",
      "Lighter periods in some cases",
      "Difficulty conceiving",
    ],
    howDiagnosed:
      "Diagnosed through a combination of AMH blood test (typically < 1.0 ng/mL), antral follicle count (AFC) on ultrasound (typically < 5–7 per ovary), and Day 3 FSH levels (elevated above 10 mIU/mL). Your doctor may also consider your age and response to previous stimulation cycles.",
    impactOnFertility:
      "DOR means fewer eggs are available for retrieval during IVF or egg freezing, which may require more cycles to achieve the desired number of eggs or embryos. However, egg quality — which matters most for pregnancy — is primarily determined by age rather than AMH or AFC alone. Many women with DOR still achieve successful pregnancies.",
    prevalence:
      "Affects roughly 10–30% of women seeking fertility treatment, increasing with age.",
    treatmentOptions: [
      "IVF with tailored stimulation protocols (mini-IVF, high-dose protocols)",
      "Egg or embryo freezing to preserve remaining fertility",
      "Donor eggs if own egg supply is critically low",
      "Supplements like CoQ10 and DHEA (evidence is moderate)",
      "Lifestyle optimization to support egg quality",
    ],
    prognosis:
      "Prognosis depends heavily on age and egg quality. Younger women with DOR (under 35) often still have good-quality eggs and reasonable success rates per embryo. Older women may need to consider multiple cycles or donor eggs.",
    forWhom: "her",
  },

  sources: [
    {
      title:
        "Testing and interpreting measures of ovarian reserve: a committee opinion",
      authors: "Practice Committee of the ASRM",
      journal: "Fertility and Sterility",
      year: 2020,
      url: "https://www.asrm.org/practice-guidance/practice-committee-documents/testing-and-interpreting-measures-of-ovarian-reserve-a-committee-opinion-2020/",
      evidenceLevel: "guideline",
    },
  ],
};

registerBlock(dor);

// ─── Endometriosis ──────────────────────────────────────────────────

const endometriosis: ContentBlock<"condition"> = {
  id: makeId("dx-endometriosis"),
  kind: "condition",
  tags: ["her", "endo", "evidence-based"],
  title: "Endometriosis",
  subtitle: "Tissue similar to the uterine lining grows outside the uterus",
  body: {
    conditionName: "Endometriosis",
    category: "structural",
    description:
      "Endometriosis occurs when tissue similar to the lining of the uterus (endometrium) grows outside the uterus — on the ovaries, fallopian tubes, bowel, or pelvic lining. This tissue responds to hormonal cycles, causing inflammation, scarring, and adhesions that can affect fertility.",
    symptoms: [
      "Painful periods (dysmenorrhea)",
      "Chronic pelvic pain",
      "Pain during or after intercourse",
      "Pain with bowel movements or urination during periods",
      "Heavy or irregular periods",
      "Fatigue",
      "Difficulty conceiving",
    ],
    howDiagnosed:
      "Definitive diagnosis requires laparoscopic surgery with biopsy. However, it is often suspected based on symptoms, pelvic exam, and imaging (ultrasound showing endometriomas/chocolate cysts). MRI can help map deep-infiltrating disease. Average diagnosis delay is 7–10 years from symptom onset.",
    impactOnFertility:
      "Endometriosis can impair fertility through multiple mechanisms: distorting pelvic anatomy, blocking fallopian tubes, creating a hostile environment for eggs and embryos, and reducing ovarian reserve (especially with endometriomas). Roughly 30–50% of women with endometriosis experience infertility.",
    prevalence:
      "Affects approximately 1 in 10 women of reproductive age worldwide.",
    treatmentOptions: [
      "Laparoscopic excision surgery to remove endometrial tissue",
      "IVF (often recommended for moderate-to-severe cases)",
      "Hormonal suppression before IVF (GnRH agonist down-regulation)",
      "IUI for mild endometriosis",
      "Anti-inflammatory diet and lifestyle modifications",
      "Pain management (NSAIDs, hormonal therapies)",
    ],
    prognosis:
      "Mild endometriosis has relatively good fertility outcomes, especially with IUI or IVF. Moderate-to-severe cases benefit from surgical treatment before fertility treatment. IVF success rates are somewhat lower with endometriosis but still promising, particularly in younger women.",
    forWhom: "her",
  },

  sources: [
    {
      title: "ESHRE guideline: endometriosis",
      authors:
        "ESHRE Endometriosis Guideline Development Group (Becker CM, Bokor A, Heikinheimo O, et al.)",
      journal: "Human Reproduction Open",
      year: 2022,
      url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC8951218/",
      evidenceLevel: "guideline",
    },
  ],
};

registerBlock(endometriosis);

// ─── Chronic Endometritis ───────────────────────────────────────────

const chronicEndometritis: ContentBlock<"condition"> = {
  id: makeId("dx-chronic-endometritis"),
  kind: "condition",
  tags: ["her", "testing", "ivf", "fet", "evidence-based"],
  title: "Chronic endometritis",
  subtitle: "Low-grade inflammation of the uterine lining",
  body: {
    conditionName: "Chronic endometritis",
    category: "reproductive",
    description:
      "Chronic endometritis is persistent, low-grade inflammation of the endometrium, the lining inside the uterus. It is different from endometriosis. It is usually linked to infection or an altered uterine microbiome and is mainly discussed in fertility care when there is recurrent implantation failure, recurrent pregnancy loss, or suspicion of uterine inflammation.",
    symptoms: [
      "Often no clear symptoms",
      "Abnormal uterine bleeding or spotting",
      "Pelvic discomfort",
      "Unusual vaginal discharge",
      "Recurrent implantation failure",
      "Recurrent pregnancy loss",
    ],
    howDiagnosed:
      "Diagnosis usually requires an endometrial biopsy. Clinicians may use pathology with plasma cell staining such as CD138, hysteroscopy findings, culture, PCR, or sequencing-based tests. Commercial tests such as ALICE look for bacterial DNA, but a detected organism should still be interpreted in clinical context.",
    impactOnFertility:
      "Chronic endometritis may affect implantation by creating inflammation in the uterine lining. Treating confirmed chronic endometritis can matter clinically, but routine commercial screening for every IVF or FET patient has not been proven to improve live birth.",
    prevalence:
      "Reported prevalence varies widely because diagnostic criteria differ, but it is found more often in recurrent implantation failure and recurrent pregnancy loss workups than in unselected fertility patients.",
    treatmentOptions: [
      "Antibiotics when chronic endometritis is confirmed or strongly suspected",
      "Repeat biopsy or reassessment if symptoms or fertility history justify it",
      "Hysteroscopy if structural uterine pathology is suspected",
      "Avoid routine test-and-treat pathways when there is no clear clinical indication",
    ],
    prognosis:
      "Prognosis depends on whether inflammation is truly present, whether the underlying cause is treated, and what other fertility factors are involved. A negative or positive commercial microbiology test should not be interpreted as a full explanation for implantation failure on its own.",
    forWhom: "her",
  },
  sources: [
    {
      title: "Good practice recommendations on recurrent implantation failure",
      journal: "Human Reproduction Open / ESHRE",
      year: 2023,
      url: "https://www.eshre.eu/Guidelines-and-Legal/Guidelines/RIF",
      evidenceLevel: "review",
    },
    {
      title:
        "Fertility problems: assessment and treatment, evidence review D, endometrial receptivity testing",
      journal: "NICE NG257",
      year: 2026,
      url: "https://www.nice.org.uk/guidance/ng257/evidence/d-endometrial-receptivity-testing-pdf-15664473329",
      evidenceLevel: "review",
    },
  ],
};

registerBlock(chronicEndometritis);

// ─── PMOS (ex PCOS) ───────────────────────────────────────────────────────────

const pcos: ContentBlock<"condition"> = {
  id: makeId("dx-pcos"),
  kind: "condition",
  tags: ["her", "pmos", "pcos", "evidence-based"],
  title: "PMOS (ex PCOS)",
  subtitle: "Hormonal disorder affecting ovulation and metabolism",
  body: {
    conditionName: "PMOS (ex PCOS)",
    category: "endocrine",
    description:
      "PMOS (ex PCOS) is a common hormonal disorder where the ovaries produce excess androgens (male hormones), leading to irregular ovulation, follicle development issues, and metabolic changes. Despite the name, not all women with PMOS (ex PCOS) have ovarian cysts — the 'cysts' are actually small immature follicles.",
    symptoms: [
      "Irregular or absent periods",
      "Heavy or unpredictable bleeding",
      "Irregular ovulation",
      "Excess facial or body hair",
      "Acne or oily skin",
      "Weight gain or difficulty losing weight",
      "Thinning scalp hair",
      "Darkened skin patches",
    ],
    howDiagnosed:
      "Diagnosed using the Rotterdam criteria — requires at least 2 of 3: irregular/absent ovulation, clinical or biochemical signs of excess androgens, and polycystic-appearing ovaries on ultrasound (≥12 small follicles per ovary or increased ovarian volume). Blood tests include testosterone, DHEA-S, insulin, glucose, and AMH.",
    impactOnFertility:
      "PMOS (ex PCOS) is one of the most treatable causes of infertility. The primary issue is irregular or absent ovulation, which means eggs aren't released predictably. However, women with PMOS (ex PCOS) often have high egg counts and can respond very well to fertility medications — though they're at higher risk for ovarian hyperstimulation syndrome (OHSS).",
    prevalence:
      "Affects 8–13% of women of reproductive age; up to 70% remain undiagnosed.",
    treatmentOptions: [
      "Letrozole (first-line for ovulation induction)",
      "Clomiphene citrate for ovulation induction",
      "Metformin (for insulin resistance)",
      "IUI with ovulation induction",
      "IVF (for resistant cases)",
      "Weight management and lifestyle changes",
      "Inositol supplements (moderate evidence)",
    ],
    prognosis:
      "PMOS (ex PCOS) has one of the best fertility prognoses among infertility diagnoses. Most women with PMOS (ex PCOS) will conceive with ovulation induction alone or with IUI. IVF, when needed, tends to produce many eggs — though careful protocols are required to avoid OHSS.",
    forWhom: "her",
  },

  sources: [
    {
      title:
        "Recommendations from the 2023 International Evidence-based Guideline for the Assessment and Management of Polycystic Ovary Syndrome",
      authors: "Teede HJ, Tay CT, Laven JJE, et al.",
      journal: "Human Reproduction",
      year: 2023,
      url: "https://academic.oup.com/humrep/article/38/9/1655/7218421",
      evidenceLevel: "guideline",
    },
  ],
};

registerBlock(pcos);

// ─── Unexplained Infertility ────────────────────────────────────────

const unexplained: ContentBlock<"condition"> = {
  id: makeId("dx-unexplained-infertility"),
  kind: "condition",
  tags: ["her", "both-partners", "evidence-based"],
  title: "Unexplained infertility",
  subtitle: "No identifiable cause found after standard testing",
  body: {
    conditionName: "Unexplained infertility",
    category: "reproductive",
    description:
      "Unexplained infertility is diagnosed when standard fertility testing (semen analysis, ovulation assessment, tubal patency, and uterine evaluation) reveals no clear cause for difficulty conceiving. It doesn't mean nothing is wrong — it means current tests haven't identified the specific factor. Subtle issues with egg quality, sperm function, fertilization, or implantation may be at play.",
    symptoms: [
      "Inability to conceive after 12 months of trying (or 6 months if over 35)",
      "Normal test results across standard fertility workup",
    ],
    howDiagnosed:
      "Diagnosed by exclusion — all standard tests return normal results: regular ovulation (confirmed by blood work or tracking), at least one open fallopian tube (HSG or laparoscopy), normal uterine cavity (HSG or saline ultrasound), and normal semen analysis.",
    impactOnFertility:
      "Despite the 'unexplained' label, cumulative pregnancy rates are reasonable. About 60% of couples with unexplained infertility will conceive within 3 years without treatment. Treatment can accelerate the timeline significantly.",
    prevalence: "Accounts for roughly 25–30% of infertility diagnoses.",
    treatmentOptions: [
      "Timed intercourse with ovulation monitoring",
      "IUI with ovulation induction (letrozole or clomid)",
      "IVF (recommended after failed IUI cycles or if prolonged)",
      "Lifestyle optimization",
    ],
    prognosis:
      "Generally favorable. IUI success rates are around 10–15% per cycle. IVF success rates are comparable to age-matched averages, often in the 40–60% range per transfer for women under 35.",
    forWhom: "both",
  },

  sources: [
    {
      title:
        "Evidence-based treatments for couples with unexplained infertility: a guideline",
      authors: "Practice Committee of the ASRM",
      journal: "Fertility and Sterility",
      year: 2020,
      url: "https://www.asrm.org/practice-guidance/practice-committee-documents/",
      evidenceLevel: "guideline",
    },
    {
      title: "Fertility problems: assessment and treatment",
      authors: "National Institute for Health and Care Excellence",
      journal: "NICE Guideline NG257",
      year: 2024,
      url: "https://www.nice.org.uk/guidance/ng257",
      evidenceLevel: "guideline",
    },
  ],
};

registerBlock(unexplained);

// ─── Premature Ovarian Insufficiency ────────────────────────────────

const poi: ContentBlock<"condition"> = {
  id: makeId("dx-premature-ovarian-insufficiency"),
  kind: "condition",
  tags: ["her", "age-factor", "evidence-based"],
  title: "Premature ovarian insufficiency (POI)",
  subtitle: "Ovarian function declines before age 40",
  body: {
    conditionName: "Premature ovarian insufficiency (POI)",
    category: "reproductive",
    description:
      "POI (formerly called premature ovarian failure) occurs when the ovaries stop functioning normally before age 40. Unlike menopause, POI can be intermittent — some women have occasional ovulation and even spontaneous pregnancies. It's associated with very low egg reserves and elevated FSH.",
    symptoms: [
      "Irregular or absent periods before age 40",
      "Hot flashes and night sweats",
      "Vaginal dryness",
      "Difficulty conceiving",
      "Low energy and mood changes",
    ],
    howDiagnosed:
      "Diagnosed when FSH levels are consistently elevated (> 25–40 mIU/mL) on two tests at least one month apart in a woman under 40 with absent or irregular periods for at least 4 months. AMH is typically very low or undetectable.",
    impactOnFertility:
      "POI significantly impacts fertility due to very few remaining eggs. Spontaneous pregnancy occurs in about 5–10% of women with POI. Donor eggs offer the highest success rates for those who want to carry a pregnancy.",
    prevalence: "Affects approximately 1 in 100 women under 40.",
    treatmentOptions: [
      "Donor eggs (highest success rate)",
      "IVF with own eggs (if any ovarian activity remains)",
      "Hormone replacement therapy for bone and heart health",
      "Embryo freezing if caught early",
      "DHEA and CoQ10 supplementation (limited evidence)",
    ],
    prognosis:
      "Fertility prognosis with own eggs is limited, though not impossible. With donor eggs, pregnancy success rates are excellent (often 50–60% per transfer). Early detection and fertility preservation are key.",
    forWhom: "her",
  },

  sources: [
    {
      title:
        "ESHRE Guideline: management of women with premature ovarian insufficiency",
      authors: "Webber L, Davies M, Anderson R, et al.",
      journal: "Human Reproduction",
      year: 2016,
      url: "https://academic.oup.com/humrep/article/31/5/926/2380079",
      evidenceLevel: "guideline",
    },
  ],
};

registerBlock(poi);
